/**
 * Lời gọi API CampaignPackage dùng cho page/container.
 * Quy ước: formatter.ts chỉ chứa hàm thuần (map/format), KHÔNG gọi API —
 * mọi fetch nằm ở đây để dễ tìm, dễ mock và dễ đổi caching.
 * File này dùng được ở cả server component lẫn client (loader phân trang).
 */
import { get } from "utils/lodash";
import CampaignPackageService from "services/campaignPackage";
import {
  CAMPAIGN_PACKAGE_SLOT_SELECTION,
  activeCampaignPackageWhere,
  isCampaignPackageLive,
} from "services/campaignPackage/constant";
import { slotDiscountByShop, SlotDiscount } from "services/campaignPackage/formatter";

export const OFFER_PAGE_SIZE = 12;

/** Trang danh sách /uu-dai (dùng chung server page + client phân trang) */
export const fetchOfferPage = async ({
  page = 1,
  limit = OFFER_PAGE_SIZE,
  category,
}: {
  page?: number;
  limit?: number;
  category?: string;
}) => {
  const where: any = activeCampaignPackageWhere("service");
  if (category) where.AND.push({ mainShopCategory: { equals: category } });
  const res = await CampaignPackageService.find({ page, limit, where, sort: "-updatedAt" });
  return {
    data: ((get(res, "data") || []) as unknown[]).filter(isCampaignPackageLive),
    total: (get(res, "params.total") as number) || 0,
  };
};

/**
 * Map shopId → deal khung giờ tốt nhất đang hiệu lực (cho dải "Săn giờ đẹp" trang chủ).
 *
 * KHÔNG tự catch lỗi ở đây (trước đây trả {} khi lỗi) — trang chủ có
 * `revalidate = 3600`: nếu nuốt lỗi và trả rỗng, Next ISR CACHE luôn bản
 * rỗng đó trong 1 giờ (dải "Săn giờ đẹp" biến mất cho mọi người xem, kể cả
 * khi CMS chỉ lỗi tạm thời vài giây). Để lỗi ném lên tận page.tsx (không
 * .catch ở đó) → ISR coi lượt revalidate này là THẤT BẠI và tiếp tục phục vụ
 * bản cache cũ (có dải, đã render tốt) thay vì ghi đè bằng bản thiếu.
 */
export const fetchSlotDealMap = async (limit = 100): Promise<Record<string, SlotDiscount>> => {
  const res = await CampaignPackageService.find(
    { where: activeCampaignPackageWhere("slot"), limit, page: 1 },
    CAMPAIGN_PACKAGE_SLOT_SELECTION,
  );
  return slotDiscountByShop(get(res, "data", []) as any[]);
};

/** Khuyến mãi khung giờ của 1 shop (trang shop) */
export const fetchShopSlotPackages = async (shopId: string) => {
  const res = await CampaignPackageService.find(
    {
      where: activeCampaignPackageWhere("slot", { shopId }),
      limit: 20,
      page: 1,
      sort: "-updatedAt",
    },
    CAMPAIGN_PACKAGE_SLOT_SELECTION,
  );
  return (get(res, "data") || []) as any[];
};

/** Ưu đãi dịch vụ còn hiệu lực của 1 shop (trang shop) */
export const fetchShopOffers = async (shopId: string) => {
  const res = await CampaignPackageService.find({
    where: activeCampaignPackageWhere("service", { shopId }),
    limit: 50,
    page: 1,
    sort: "-updatedAt",
  });
  return ((get(res, "data") || []) as unknown[]).filter(isCampaignPackageLive);
};
